/**
 * `StepProgressChecklist` — vertical 3-step checklist that replaces the
 * 3-dot spinner inside an in-flight v2 chat bubble.
 *
 * Renders one row per step with state-driven typography:
 *   - pending → text.secondary
 *   - running → bold + text.primary + animated text shimmer
 *               (background-clip:text gradient sweep, matching the SOW
 *               verification ``StepProgressSpinner`` pattern)
 *   - done    → strikethrough + text.secondary
 *   - failed  → bold + error.main
 *
 * A single list-level CircularProgress sits to the left of the entire
 * list whenever ANY step is in the running state. The slot is vertically
 * centered relative to the full list so the spinner stays mid-bubble
 * rather than anchored to the first row.
 *
 * Optional top-level `error` renders as a small inline message under the
 * failed row in `error.main`.
 *
 * See: issues/20260505-turn-progress-checklist.md §4.2.1, §8
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { keyframes, styled } from '@mui/system';
import type { ProgressErrorShape, ProgressStep } from './useTurnProgress';

interface Props {
  steps: ProgressStep[];
  error?: ProgressErrorShape | null;
  /** An action that applies to the active work, rendered beside its spinner. */
  trailingAction?: React.ReactNode;
  /**
   * Optional client-only lead step rendered FIRST, above the server-driven
   * reader steps. Used for the "Reading your question" phase that runs
   * during the relevance/clarification gate — a window that happens BEFORE
   * the backend ever writes a progress envelope, so it cannot be a server
   * step. When provided, it participates in the list exactly like a normal
   * step (state-driven typography + the shared list-level spinner).
   */
  leadStep?: ProgressStep | null;
}

// Horizontal text-shimmer sweep, mirroring the SOW verification
// `StepProgressSpinner` running-step style. Uses background-clip:text +
// transparent fill so the gradient masks the glyphs themselves rather
// than painting a stripe behind them.
const stepShimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Apply shimmer via a styled component rather than the `sx` prop because
// MUI's `sx` pipeline silently drops vendor-prefixed properties like
// `WebkitBackgroundClip` and `WebkitTextFillColor`, leaving bold without
// the gradient sweep. `styled()` passes the CSS through emotion directly.
const StepText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'stepState',
})<{ stepState: ProgressStep['state'] }>(({ theme, stepState }) => {
  const palette = (theme as { palette?: any } | undefined)?.palette;
  const textPrimary = palette?.text?.primary ?? 'inherit';
  const textSecondary = palette?.text?.secondary ?? 'inherit';
  const errorMain = palette?.error?.main ?? 'inherit';
  // Shimmer glint tracks the mode's base surface so it reads as a sweep in
  // both light and dark (a hardcoded white band would vanish on dark text).
  const shimmerGlint = palette?.background?.default ?? 'transparent';
  const base = {
    fontSize: '0.85rem',
    lineHeight: 1.3,
  };
  switch (stepState) {
    case 'running':
      // Same gradient as the SOW verification spinner: solid at the ends,
      // a bright ~95%-white slot at 50% that sweeps across the glyphs.
      return {
        ...base,
        fontWeight: 600,
        color: textPrimary,
        background: `linear-gradient(90deg, currentColor 0%, currentColor 40%, ${shimmerGlint} 50%, currentColor 60%, currentColor 100%)`,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animation: `${stepShimmer} 1.8s linear infinite`,
      };
    case 'failed':
      return { ...base, fontWeight: 600, color: errorMain };
    case 'done':
      return { ...base, fontWeight: 400, color: textSecondary, textDecoration: 'line-through' };
    case 'pending':
    default:
      return { ...base, fontWeight: 400, color: textSecondary };
  }
});

const StepProgressChecklist: React.FC<Props> = ({ steps, error, leadStep, trailingAction }) => {
  // The lead step (when present) is a real row in the list, so its
  // running state must also drive the shared list-level spinner.
  const allSteps = leadStep ? [leadStep, ...steps] : steps;
  const hasRunningStep = allSteps.some((s) => s.state === 'running');

  return (
    <Box
      data-testid="step-progress-checklist"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        // Vertically center the spinner slot relative to the full list
        // (3 rows) instead of anchoring it to the first row's baseline.
        alignItems: 'center',
        gap: 1.25,
        py: 0.25,
      }}
    >
      {/* List-level spinner: a single, larger CircularProgress to the left
          of the whole list, visible whenever any step is currently running.
          The slot is centered with the list via the parent's
          `alignItems: center` so it sits mid-bubble. */}
      <Box
        data-testid="list-level-spinner-slot"
        data-active={hasRunningStep ? 'true' : 'false'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          minHeight: 24,
          flexShrink: 0,
          // Reserve the slot even when no step is running so layout
          // doesn't shift between updates.
          visibility: hasRunningStep ? 'visible' : 'hidden',
        }}
      >
        {hasRunningStep && (
          <CircularProgress
            size={22}
            thickness={4.5}
            data-testid="list-level-spinner"
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        {allSteps.map((step) => {
          const isRunning = step.state === 'running';
          const isFailed = step.state === 'failed';

          return (
            <React.Fragment key={step.step_id}>
              <Box
                data-testid={`step-row-${step.step_id}`}
                data-state={step.state}
                data-running={isRunning ? 'true' : 'false'}
                className={isRunning ? 'step-running' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  minHeight: 18,
                }}
              >
                <StepText
                  variant="body2"
                  stepState={step.state}
                  data-testid={`step-text-${step.step_id}`}
                  data-running={isRunning ? 'shimmer' : undefined}
                  className={isRunning ? 'step-shimmer-text' : undefined}
                >
                  {step.label}
                </StepText>
              </Box>
              {isFailed && error && error.step_id === step.step_id && (
                <Typography
                  data-testid={`step-error-${step.step_id}`}
                  variant="caption"
                  sx={{
                    color: 'error.main',
                    fontSize: '0.75rem',
                    lineHeight: 1.3,
                  }}
                >
                  {error.message}
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {trailingAction && (
        <Box
          data-testid="step-progress-trailing-action"
          sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          {trailingAction}
        </Box>
      )}
    </Box>
  );
};

export default StepProgressChecklist;
